/**
 * De lagenstapel (Vandaag v2, fase 5; docs/heute-vandaag-v2-plan.md): alles
 * wat boven een scherm open gaat, een tray of de plan-lade, is een laag. Er
 * is altijd één bovenste laag, en alleen die luistert:
 * - Escape sluit de bovenste laag, nooit twee tegelijk.
 * - Tab blijft binnen de bovenste laag.
 * - Een laag eronder die niet om de bovenste heen ligt, wordt inert en
 *   aria-hidden (useLaag geeft erBoven). Een tray binnen de lade ligt in de
 *   DOM ín de lade (slots, geen portals), dus de lade blijft dan bereikbaar
 *   voor de tray: daar dekken aria-modal en de Tab-val het af.
 * - Bij openen gaat de focus naar de sluitknop van de laag
 *   ([data-laag-sluit]); bij sluiten keert hij een frame later terug naar
 *   wat hem opende (een frame later: tot dan kan dat nog inert zijn). Een
 *   laag die in dezelfde commit een andere vervangt, neemt diens opener
 *   over (zie overdracht). Gaf de laag bij het openen zelf al iets de
 *   focus (een kop), dan is de opener waar die focus vandaan kwam (zie
 *   laatsteWissel). Ging de laag open vanuit een laag die zelf sluit
 *   (een keuze in het ⋯-menu van een rij), dan erft hij diens opener (zie
 *   erfOpener). Verdween de opener terwijl de laag open stond, dan
 *   krijgt het vak eromheen met [data-laag-terugvak] de focus (zie
 *   LAGEN.terugVak), en zonder zo'n vak de terugval rond de laag: de kop
 *   van de pagina met [data-laag-terugval]. Zegt de laag zelf dat zijn
 *   opener er niet meer bij hoort (laagNaarTerugval), dan krijgt die
 *   terugval de focus ook als de opener er nog staat.
 * - Zolang er een laag open is, staat html[data-lagen] en scrolt de pagina
 *   eronder niet mee (heute.css); een schuifbalk die daardoor verdwijnt,
 *   komt terug als opvulling (--lagen-balk), zodat niets opzij schuift.
 * Volgorde: eerst de soort (de lade ligt altijd onder trays, ook als een
 * tray bij een koude treffer eerder registreert), dan de volgorde van openen.
 *
 * Slots, geen portals: elke laag staat in de DOM naast (nooit binnen) een
 * element dat een transform krijgt. Zo rendert een diepe link op de server
 * goed en hoeft er na hydratie niets te verhuizen. Daarom draagt het paneel
 * van de lade in rust nooit een transform (geen forwards-vulling op de
 * ingang, geen will-change of contain): een tray erin is position: fixed
 * tegen het scherm.
 *
 * Wat pakket B (PlanLade) en D (Vandaag) gebruiken, staat in LAGEN hieronder:
 * - .plan-lade-laag: de wortel van de lade (fixed, z-index 150, boven de
 *   menubalk; trays erin op 200). Draagt data-uit tijdens de uitgang
 *   (useUitgang().uit), data-weg zodra die klaar is (useUitgang().weg) en
 *   data-rust als hij al open was bij de hydratie (useRust(open) && !uit:
 *   geen ingang). Dit element is de ref voor
 *   useLaag({ open: !weg, soort: 'lade', onEscape: start }).
 *   - .plan-lade-scrim: de scrim (kleur van de tray-scrim); klik = start.
 *   - .plan-lade: het paneel, role=dialog aria-modal. Eigen scrollvlak
 *     (overscroll-behavior: contain), bovenaan --size-lade-top onder de
 *     safe area vrij. De uitgang loopt op dit element: het is de ref voor
 *     useUitgang({ animatie: LADE_UIT }), en daar hoort ook diens
 *     onAnimationEnd op.
 *   - .plan-lade-kop: optionele kopregel die bovenin het paneel blijft
 *     staan; de sluitknop is .tray-x met data-laag-sluit (de focus landt
 *     daar bij openen, en terug als een tray erin dichtgaat zonder opener).
 * - .vandaag-achter: de wrapper om Vandaags <main> (zonder trays en zonder
 *   menubalk: alles met position: fixed staat ernaast). Met data-wijkt wijkt
 *   hij terug (translateY(--wijk-shift) scale(--wijk-scale)), rond de
 *   bovenrand van het scherm: zet --wijk-y inline op `${scrollY}px` op het
 *   moment dat de lade opengaat. Onder verminderde beweging wijkt hij niet.
 *   inert + aria-hidden zet D zelf, op deze wrapper en op een wrapper om de
 *   menubalk (die wijkt niet terug), zolang useLagenOpen() > 0 (of de lade
 *   er al is: op de server is de stapel nog leeg).
 *
 * Geen DOM-toegang bij het importeren: de stapel bestaat alleen in de
 * browser, en registreert pas in effecten (de server ziet een lege stapel).
 */
import {
	type AnimationEvent,
	type RefObject,
	useCallback,
	useEffect,
	useId,
	useLayoutEffect,
	useRef,
	useState,
	useSyncExternalStore,
} from 'react'

/** Klassen, attributen en animatienamen die de lagen delen (heute.css). */
export const LAGEN = {
	ladeLaag: 'plan-lade-laag',
	ladeScrim: 'plan-lade-scrim',
	lade: 'plan-lade',
	ladeKop: 'plan-lade-kop',
	achter: 'vandaag-achter',
	/** data-wijkt op .vandaag-achter: Vandaag wijkt terug achter de lade. */
	wijkt: 'data-wijkt',
	/** Inline custom property op .vandaag-achter: scrollY in px bij openen. */
	wijkY: '--wijk-y',
	/** data-uit op een laag: de uitgang speelt. */
	uit: 'data-uit',
	/** data-weg op een laag: de uitgang is klaar, de laag wacht alleen nog
	 *  op de navigatie die hem sluit (onzichtbaar en niet aan te tikken). */
	weg: 'data-weg',
	/** data-rust op een laag: al open bij de hydratie, dus geen ingang. */
	rust: 'data-rust',
	/** data-wissel op een laag: hij nam in dezelfde commit de plaats in van
	 *  een andere (useWissel), dus het scherm dimt al: de scrim komt niet
	 *  opnieuw binnen. */
	wissel: 'data-wissel',
	/** data-laag-sluit op de sluitknop: daar landt de focus bij openen. */
	sluit: 'data-laag-sluit',
	/** data-laag-terugvak op een vak (met tabIndex -1) om een opener die
	 *  kan verdwijnen terwijl zijn laag open staat: Zet iets op de lijst in
	 *  het neem-mee-paneel staat er alleen bij een lege lijst, en de lade
	 *  erboven zet er iets op. Is de opener bij het sluiten weg, dan krijgt
	 *  dit vak de focus, niet <body> of de sluitknop van de lade. */
	terugVak: 'data-laag-terugvak',
	/** data-laag-terugval op de kop van een pagina (met tabIndex -1): daar
	 *  gaat de focus heen als een laag erboven zegt dat zijn opener er niet
	 *  meer bij hoort (laagNaarTerugval), of als zijn opener verdween en er
	 *  geen terugvak om stond. Hij scrolt dan in beeld: anders stond de
	 *  focus buiten het scherm. */
	terugval: 'data-laag-terugval',
	/** Op <html> zolang er een laag open is: de pagina scrolt niet. */
	html: 'data-lagen',
	/** Op <html> zolang er een laag open is: de breedte van de schuifbalk
	 *  die daardoor verdween, als opvulling (heute.css). */
	balk: '--lagen-balk',
} as const

/** De uitgang van de tray (keyframe in heute.css). */
export const TRAY_UIT = 'trayDown'
/** De uitgang van het lade-paneel (keyframe in heute.css). */
export const LADE_UIT = 'ladeAf'
/** De ingang van de plan-lade (keyframe in heute.css). */
export const LADE_OP = 'ladeOp'

export type LaagSoort = 'lade' | 'tray'

/** Waar Tab op kan landen: textareas wel, verborgen inputs (type="hidden",
 *  of een `hidden` file-input achter een knop) niet. */
const FOCUSBAAR = [
	'button',
	'[href]',
	'input:not([type="hidden"])',
	'select',
	'textarea',
	'[tabindex]:not([tabindex="-1"])',
].join(', ')

export function focusables(root: HTMLElement) {
	return Array.from(root.querySelectorAll<HTMLElement>(FOCUSBAAR)).filter(
		(el) =>
			!el.hasAttribute('disabled') &&
			!el.closest('[inert]') &&
			// display:none (het `hidden`-attribuut, een ingeklapte stap) heeft
			// geen vak
			el.getClientRects().length > 0,
	)
}

/* ───────────────────────────── De stapel ───────────────────────────── */

type Laag = {
	id: string
	soort: LaagSoort
	volg: number
	ref: RefObject<HTMLElement | null>
	escape: { current: () => void }
	/** Wat de focus had toen de laag openging. */
	terug: HTMLElement | null
	/** Het vak om `terug` met [data-laag-terugvak], als dat er is: de focus
	 *  gaat daarheen als `terug` verdween terwijl de laag open stond. */
	terugVak: HTMLElement | null
	/** De terugval rond de laag ([data-laag-terugval]), als de laag zei dat
	 *  zijn opener er niet meer bij hoort (laagNaarTerugval): de focus gaat
	 *  dan daarheen, niet naar `terug`. */
	terugval: HTMLElement | null
	/** De terugval rond de laag, gezocht bij het openen: de focus gaat
	 *  daarheen als `terug` verdween en er geen terugvak is. Bij het sluiten
	 *  is de wortel van de laag soms al weg (PlanTrays ruimt de Wijzig-lade
	 *  op), dus niet pas dan zoeken. */
	vangnet: HTMLElement | null
	/** De wortel van de laag bij het aanmelden. Bij unmount is de ref al
	 *  leeg, en meldAf moet nog weten wat erin lag (zie erfOpener). */
	wortel: HTMLElement | null
}

/** 'boven': de bovenste laag. 'onder': er ligt een laag boven die er niet
 *  binnen valt (inert + aria-hidden). 'open': open, niet bovenaan, maar de
 *  laag erboven ligt erin (een tray in de lade). '': niet open. */
type Stand = 'boven' | 'onder' | 'open' | ''

const RANG: Record<LaagSoort, number> = { lade: 0, tray: 1 }

let lagen: Laag[] = []
let standen = new Map<string, Stand>()
let volgnummer = 0
const luisteraars = new Set<() => void>()

function abonneer(luisteraar: () => void) {
	luisteraars.add(luisteraar)
	return () => {
		luisteraars.delete(luisteraar)
	}
}

function bovenste(): Laag | undefined {
	return lagen[lagen.length - 1]
}

function herbereken() {
	const nieuw = new Map<string, Stand>()
	lagen.forEach((laag, i) => {
		const wortel = laag.ref.current
		const bedekt = lagen.slice(i + 1).some((erboven) => {
			const el = erboven.ref.current
			return !wortel || !el || !wortel.contains(el)
		})
		const top = i === lagen.length - 1
		nieuw.set(laag.id, top ? 'boven' : bedekt ? 'onder' : 'open')
	})
	standen = nieuw
	for (const luisteraar of luisteraars) luisteraar()
}

function focusSluit(root: HTMLElement | null) {
	if (!root) return
	const doel =
		root.querySelector<HTMLElement>(`[${LAGEN.sluit}]`) ?? focusables(root)[0]
	doel?.focus({ preventScroll: true })
}

function bruikbaar(el: HTMLElement | null): el is HTMLElement {
	return Boolean(el && el.isConnected && !el.closest('[inert]'))
}

/** De toast ligt naast de lagen (geen portal erin), maar zijn knop hoort bij
 * de Tab-ronde: wie net iets deed in een tray, moet het terug kunnen zetten. */
const TOAST_PLEK = '.heute-toast-plek'

function toastKnoppen() {
	return Array.from(document.querySelectorAll<HTMLElement>(TOAST_PLEK)).flatMap(
		(plek) => focusables(plek),
	)
}

function houdTabBinnen(event: KeyboardEvent, root: HTMLElement | null) {
	if (!root) return
	const binnen = focusables(root)
	const items = [...binnen, ...toastKnoppen()]
	if (items.length === 0) return
	const actief = document.activeElement as HTMLElement | null
	const plek = actief ? items.indexOf(actief) : -1
	if (plek < 0) {
		// de focus viel uit de laag (een knop verdween): terug erin
		event.preventDefault()
		;(event.shiftKey ? items[items.length - 1]! : items[0]!).focus()
		return
	}
	// binnen de laag doet de browser het zelf; alleen op de randen en vanuit
	// de toast sturen we, rond via de toastknop terug naar het begin
	const opRand = event.shiftKey
		? actief === binnen[0]
		: actief === binnen[binnen.length - 1]
	if (!opRand && root.contains(actief)) return
	event.preventDefault()
	const stap = event.shiftKey ? -1 : 1
	items[(plek + stap + items.length) % items.length]!.focus()
}

function opToets(event: KeyboardEvent) {
	const top = bovenste()
	if (!top || event.defaultPrevented || event.isComposing) return
	if (event.key === 'Escape') top.escape.current()
	else if (event.key === 'Tab') houdTabBinnen(event, top.ref.current)
}

function zetHtml() {
	const html = document.documentElement
	const open = lagen.length > 0
	if (open === html.hasAttribute(LAGEN.html)) return
	if (open) {
		// meten vóór het vastzetten: daarna is de schuifbalk weg
		const balk = Math.max(0, window.innerWidth - html.clientWidth)
		html.style.setProperty(LAGEN.balk, `${balk}px`)
	} else {
		html.style.removeProperty(LAGEN.balk)
	}
	html.toggleAttribute(LAGEN.html, open)
}

/**
 * De opener van een laag die net afmeldde. Neemt een laag in dezelfde
 * commit zijn plaats in (de maak-tray opent de deel-tray of de lade, de
 * lade mount opnieuw voor een nieuw plan), dan is de knop die wisselde al
 * weg en staat de focus op <body>: dan neemt de nieuwe laag deze opener
 * over. React ruimt de effecten van een commit op vóór het de nieuwe
 * draait, dus meldAf komt eerst; na de commit (een microtask) is hij weg.
 */
let overdracht: HTMLElement | null = null

/**
 * De laatste focuswissel in het document: waar de focus heen ging en waar
 * hij vandaan kwam (focusin en zijn relatedTarget). Een laag meldt zich pas
 * in een effect aan; gaf zijn inhoud in dezelfde commit al iets de focus
 * (de kop van een stap in een layout-effect, autoFocus), dan staat de focus
 * bij het aanmelden al in de laag zelf. De opener is dan waar die focus
 * vandaan kwam. Het spoor gaat aan bij de eerste useFocusSpoor (de wortel
 * van de app) of useLaag in de browser (geen DOM-toegang bij het
 * importeren). Pas bij de eerste useLaag is te laat: een luie laag (de
 * Wijzig-lade op de planpagina, de deel-tray) mount pas bij het openen, en
 * dan is de focuswissel naar zijn kop al voorbij.
 */
let laatsteWissel: {
	naar: EventTarget | null
	van: EventTarget | null
} | null = null
let spoorAan = false

function volgFocus() {
	if (spoorAan) return
	spoorAan = true
	document.addEventListener(
		'focusin',
		(event) => {
			laatsteWissel = { naar: event.target, van: event.relatedTarget }
		},
		true,
	)
}

/** Wat de laag opende. `actief` staat in de laag zelf: dan waar die focus
 *  vandaan kwam, als dat buiten de laag ligt en er nog staat. */
function openerVan(actief: HTMLElement, wortel: HTMLElement | null) {
	if (!wortel?.contains(actief)) return actief
	const van = laatsteWissel?.naar === actief ? laatsteWissel.van : null
	return van instanceof HTMLElement && van.isConnected && !wortel.contains(van)
		? van
		: null
}

function registreer(
	laag: Omit<
		Laag,
		'volg' | 'terug' | 'terugVak' | 'terugval' | 'vangnet' | 'wortel'
	>,
): Laag {
	const actief = document.activeElement
	const kwijt =
		!(actief instanceof HTMLElement) ||
		actief === document.body ||
		!actief.isConnected
	const terug = kwijt
		? overdracht?.isConnected
			? overdracht
			: null
		: openerVan(actief, laag.ref.current)
	const nieuw: Laag = {
		...laag,
		volg: ++volgnummer,
		terug,
		terugVak: terug?.closest<HTMLElement>(`[${LAGEN.terugVak}]`) ?? null,
		terugval: null,
		vangnet: laag.ref.current ? terugvalRond(laag.ref.current) : null,
		wortel: laag.ref.current,
	}
	if (lagen.length === 0) document.addEventListener('keydown', opToets)
	lagen = [...lagen, nieuw].sort(
		(a, b) => RANG[a.soort] - RANG[b.soort] || a.volg - b.volg,
	)
	zetHtml()
	herbereken()
	// alleen een laag die bovenaan komt, pakt de focus: een lade die bij een
	// koude treffer ná zijn eigen tray registreert, laat hem bij de tray
	if (bovenste() === nieuw) focusSluit(nieuw.ref.current)
	return nieuw
}

/**
 * `weg` is de wortel van de laag die net afmeldde, als die nog in de DOM
 * staat: een laag die na zijn uitgang nog op de navigatie wacht
 * (useUitgang().weg). Focus daarin telt als kwijt: die laag is onzichtbaar.
 * `vakken`: wat de plaats van de opener inneemt als die er niet meer
 * staat, op volgorde (het vak om de opener met [data-laag-terugvak], dan
 * de terugval rond de laag); de eerste die er nog staat. Na
 * laagNaarTerugval is de opener null en de terugval het enige vak.
 */
function herstelFocus(
	opener: HTMLElement | null,
	vakken: ReadonlyArray<HTMLElement | null>,
	pogingen: number,
	weg: HTMLElement | null,
) {
	const vak = vakken.find((el) => el?.isConnected) ?? null
	const terug = !opener?.isConnected && vak ? vak : opener
	const root = bovenste()?.ref.current ?? null
	const actief = document.activeElement
	const kwijt =
		!actief ||
		actief === document.body ||
		!actief.isConnected ||
		Boolean(weg?.contains(actief))
	if (root) {
		if (!kwijt && root.contains(actief)) return
		if (bruikbaar(terug) && root.contains(terug)) geefFocus(terug)
		else focusSluit(root)
		return
	}
	// iemand anders heeft de focus al gepakt (een klik, een nieuwe pagina)
	if (!kwijt) return
	if (!terug || !terug.isConnected) return
	if (terug.closest('[inert]')) {
		// de achtergrond is nog inert tot de render na het sluiten
		if (pogingen > 1)
			requestAnimationFrame(() =>
				herstelFocus(opener, vakken, pogingen - 1, weg),
			)
		return
	}
	geefFocus(terug)
}

/** De focus terug, zonder te scrollen: wat de laag opende, stond in beeld.
 *  De terugval (de kop van de pagina) kan erbuiten staan: die scrolt mee. */
function geefFocus(el: HTMLElement) {
	el.focus({ preventScroll: !el.hasAttribute(LAGEN.terugval) })
}

/**
 * De laag om `binnen` gaat bij het sluiten niet terug naar zijn opener: die
 * hoort er niet meer bij, ook als hij er nog staat. De Wijzig-lade na een
 * opslag die zei dat wijzigen niet meer kan (besluit D2): Wijzig hoort dan
 * niet meer in de balk, ook als het plan bij het herlezen nog niet voorbij
 * is, en een knop die React op dezelfde plek hergebruikt, is niet meer wat
 * de laag opende. De focus
 * gaat dan naar de terugval rond de laag: het dichtstbijzijnde element met
 * [data-laag-terugval] buiten de laag (de kop met de titel van het plan),
 * op de pagina en in de lade. Is er geen terugval, dan blijft alles zoals
 * het was. Alleen voor een laag die al open is (aangemeld), vanuit iets
 * dat erin staat.
 */
export function laagNaarTerugval(binnen: HTMLElement): void {
	const laag = [...lagen].reverse().find((l) => l.ref.current?.contains(binnen))
	const wortel = laag?.ref.current
	if (!laag || !wortel) return
	const terugval = terugvalRond(wortel)
	if (terugval) laag.terugval = terugval
}

/** Het dichtstbijzijnde element met [data-laag-terugval] buiten de laag
 *  met deze wortel (op de pagina: de kop met de titel; in de lade: die in
 *  de lade), of null. */
function terugvalRond(wortel: HTMLElement): HTMLElement | null {
	for (let el = wortel.parentElement; el; el = el.parentElement) {
		const terugval = Array.from(
			el.querySelectorAll<HTMLElement>(`[${LAGEN.terugval}]`),
		).find((kandidaat) => !wortel.contains(kandidaat))
		if (terugval) return terugval
	}
	return null
}

/**
 * Een laag die sluit, laat zijn opener na aan de lagen die vanuit hem
 * opengingen. Een keuze in het ⋯-menu van een rij (VeegRij) sluit het menu
 * en opent in dezelfde tik een andere laag; die zag de keuze als zijn
 * opener, maar de keuze verdwijnt met het menu. Hij erft dan wat het menu
 * opende (de ⋯), zodat de focus daar terugkomt als hij sluit. Had het menu
 * zelf geen opener (een diepe link), dan erft hij niets en houdt hij zijn
 * eigen terugweg (het vak of de terugval rond de laag).
 */
function erfOpener(laag: Laag) {
	const wortel = laag.ref.current ?? laag.wortel
	const erf = laag.terugval ?? laag.terug
	if (!wortel || !erf) return
	for (const ander of lagen) {
		if (ander === laag || !ander.terug || !wortel.contains(ander.terug)) {
			continue
		}
		ander.terug = erf
		ander.terugVak = laag.terugval ? null : laag.terugVak
	}
}

function meldAf(laag: Laag) {
	erfOpener(laag)
	const terug = laag.terugval ?? laag.terug
	if (terug) {
		overdracht = terug
		queueMicrotask(() => {
			if (overdracht === terug) overdracht = null
		})
	}
	lagen = lagen.filter((l) => l !== laag)
	if (lagen.length === 0) document.removeEventListener('keydown', opToets)
	zetHtml()
	herbereken()
	// na een uitgang staat de laag er nog (onzichtbaar); bij unmount is de
	// ref al leeg
	const weg = laag.ref.current
	const { terugval } = laag
	// zonder opener (een diepe link) pakt geen vak de focus: dan zoals het was
	const vakken = laag.terug ? [laag.terugVak, laag.vangnet] : []
	requestAnimationFrame(() =>
		terugval
			? herstelFocus(null, [terugval], 3, weg)
			: herstelFocus(laag.terug, vakken, 3, weg),
	)
}

/* ───────────────────────────── Hooks ───────────────────────────── */

/**
 * Meldt een laag aan zolang `open` waar is (ook tijdens de uitgang: dan
 * blijft hij bovenaan tot de uitgang klaar is; geef daarom
 * `open && !weg` mee, zie useUitgang). `ref` is de wortel van de laag:
 * de Tab-val, de focus bij openen en de vraag "ligt de laag erboven hierin?"
 * lezen hem. `onEscape` mag elke render een nieuwe functie zijn.
 */
export function useLaag(o: {
	open: boolean
	soort: LaagSoort
	onEscape: () => void
	ref: RefObject<HTMLElement | null>
}): { boven: boolean; erBoven: boolean } {
	const id = useId()
	const escape = useRef(o.onEscape)
	escape.current = o.onEscape
	const { open, soort, ref } = o

	// het spoor van de focus (zie laatsteWissel), vóór er iets opengaat
	useFocusSpoor()

	useEffect(() => {
		if (!open) return
		volgFocus()
		const laag = registreer({ id, soort, ref, escape })
		return () => meldAf(laag)
	}, [open, soort, id, ref])

	const stand = useSyncExternalStore(
		abonneer,
		() => standen.get(id) ?? '',
		() => '' as Stand,
	)
	return { boven: stand === 'boven', erBoven: stand === 'onder' }
}

/** Zet het spoor van de focus aan (zie laatsteWissel). De wortel van de app
 *  roept hem aan, zodat ook een luie laag zijn opener kent. */
export function useFocusSpoor() {
	useEffect(() => {
		volgFocus()
	}, [])
}

/** Hoeveel lagen er open zijn (0 op de server en bij de hydratie). */
export function useLagenOpen(): number {
	return useSyncExternalStore(
		abonneer,
		() => lagen.length,
		() => 0,
	)
}

const geenAbonnement = () => () => {}

/** false op de server en in de hydratie-render, daarna true. */
export function useHydrated(): boolean {
	return useSyncExternalStore(
		geenAbonnement,
		() => true,
		() => false,
	)
}

/**
 * Was deze laag al open toen de pagina hydrateerde (een diepe link, een
 * push)? Dan geen ingang (data-rust). Blijft staan tot de laag een keer dicht
 * is geweest; een laag die na de hydratie opengaat, komt gewoon binnen. De
 * server en de hydratie-render zeggen hetzelfde, dus geen hydratieverschil.
 */
export function useRust(open: boolean): boolean {
	const hydrated = useHydrated()
	const [rust, setRust] = useState(() => open && !hydrated)
	if (rust && !open) setRust(false)
	return rust
}

/**
 * Neemt deze laag bij het openen de plaats in van een andere, in dezelfde
 * commit (de maak-tray wordt de deel-tray, de lade mount opnieuw voor een
 * nieuw plan)? Dan was het scherm al gedimd en komt de scrim niet opnieuw
 * binnen (render dat als data-wissel). Te zien in een layout-effect: de
 * wortel van de oude laag is dan al uit de DOM, maar de stapel ruimt hem
 * pas na de commit op (meldAf draait in een gewoon effect). Het layout-
 * effect rendert opnieuw vóór de browser tekent, dus de scrim begint nooit
 * aan zijn fade.
 */
export function useWissel(open: boolean): boolean {
	const [wissel, setWissel] = useState(false)
	if (wissel && !open) setWissel(false)
	useLayoutEffect(() => {
		if (open && laagWisselt()) setWissel(true)
	}, [open])
	return wissel
}

/** Neemt wat nu opengaat de plaats in van een andere laag (useWissel)? Waar
 *  in een layout-effect bij het openen, net als in useWissel; voor wie het
 *  moet weten vóór useWissel opnieuw rendert (de groei, lade-bron.ts). */
export function laagWisselt(): boolean {
	return lagen.some((laag) => !laag.ref.current?.isConnected)
}

/** De langste animatie (duur plus vertraging) op het element, in ms. */
export function animatieDuur(el: HTMLElement): number {
	const stijl = getComputedStyle(el)
	const ms = (lijst: string) =>
		lijst.split(',').map((ruw) => {
			const t = ruw.trim()
			const getal = parseFloat(t)
			if (Number.isNaN(getal)) return 0
			return t.endsWith('ms') ? getal : getal * 1000
		})
	const duren = ms(stijl.animationDuration)
	const wachten = ms(stijl.animationDelay)
	return Math.max(0, ...duren.map((d, i) => d + (wachten[i] ?? 0)))
}

/**
 * De uitgang van een laag. start() zet `uit` (render dat als data-uit); de
 * CSS speelt de uitgang, en als die op `ref` klaar is (animationend van de
 * animatie met naam `animatie`, niet van een kind dat opbubbelt) roept dit
 * één keer onKlaar aan. Als vangnet loopt een timer op de berekende duur
 * maal anderhalf: een verborgen tabblad, een animatie die niet start. Onder
 * verminderde beweging is de duur 1 ms (heute-tokens.css), dus niets wacht
 * op een lange timer.
 *
 * Daarna is de laag `weg` (render dat als data-weg). Een sluiting via de
 * URL wacht nog op de loaders, en tot dan staat de laag er nog. Maar weg
 * is weg: onzichtbaar en niet aan te tikken (heute.css), en de stapel laat
 * hem los (geef useLaag `open && !weg`), zodat Escape, Tab, de focus, het
 * scrollslot en wat inert was meteen terug zijn, niet pas als de navigatie
 * klaar is. `uit` en `weg` blijven staan tot `open` onwaar wordt; pas
 * daarna begint een volgende opening schoon.
 *
 * De waakhond: blijft de laag `weg` terwijl `open` waar blijft, dan is de
 * navigatie die hem sluit niet gekomen (onderbroken, of sluit() wist niet
 * waar de browser stond). Zonder hem staat de laag onzichtbaar in de URL,
 * en doet de knop die hem opent niets meer. Na WAAKHOND_MS vraagt hij
 * onKlaar nog één keer; dat is veilig, want sluiten doet niets zolang de
 * eerste navigatie nog loopt. Is de laag na WAAKHOND_TERUG_MS nog open,
 * dan komt hij terug: zichtbaar en weer te sluiten.
 */
export const WAAKHOND_MS = 2000
export const WAAKHOND_TERUG_MS = 8000

export function useUitgang(o: {
	open: boolean
	animatie: string
	ref: RefObject<HTMLElement | null>
	onKlaar: () => void
	/** Vlak voor de uitgang begint, als data-uit er al staat maar de
	 *  browser nog niet tekende: de groei meet hier waar hij heen krimpt
	 *  (lade-bron.ts). Vóór de duur gemeten wordt, dus die klopt. */
	voorUitgang?: (el: HTMLElement) => void
}): {
	uit: boolean
	weg: boolean
	start: () => void
	onAnimationEnd: (event: AnimationEvent<HTMLElement>) => void
} {
	const { open, animatie, ref } = o
	const [uit, setUit] = useState(false)
	const [weg, setWeg] = useState(false)
	if ((uit || weg) && !open) {
		setUit(false)
		setWeg(false)
	}

	const klaarRef = useRef(o.onKlaar)
	klaarRef.current = o.onKlaar
	const voorRef = useRef(o.voorUitgang)
	voorRef.current = o.voorUitgang
	const openRef = useRef(open)
	openRef.current = open
	const uitRef = useRef(uit)
	uitRef.current = uit
	const gedaan = useRef(false)

	const klaar = useCallback(() => {
		if (gedaan.current) return
		gedaan.current = true
		setWeg(true)
		klaarRef.current()
	}, [])

	const start = useCallback(() => {
		if (openRef.current) setUit(true)
	}, [])

	useLayoutEffect(() => {
		if (!uit) return
		gedaan.current = false
		const el = ref.current
		if (el) voorRef.current?.(el)
		const timer = window.setTimeout(klaar, el ? animatieDuur(el) * 1.5 : 0)
		return () => window.clearTimeout(timer)
	}, [uit, ref, klaar])

	useEffect(() => {
		if (!weg || !open) return
		const nogEens = window.setTimeout(() => klaarRef.current(), WAAKHOND_MS)
		const terug = window.setTimeout(() => {
			gedaan.current = false
			setUit(false)
			setWeg(false)
		}, WAAKHOND_MS + WAAKHOND_TERUG_MS)
		return () => {
			window.clearTimeout(nogEens)
			window.clearTimeout(terug)
		}
	}, [weg, open])

	const onAnimationEnd = useCallback(
		(event: AnimationEvent<HTMLElement>) => {
			if (event.target !== event.currentTarget) return
			if (event.animationName !== animatie || !uitRef.current) return
			klaar()
		},
		[animatie, klaar],
	)

	return { uit, weg, start, onAnimationEnd }
}
